import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PAGE_NAME_DISCLAIMER,
  PAGE_NAME_LOGIN,
  PAGE_NAME_TOP,
} from "../../constants/common_constants";
import { useApi, useErrorDialog } from "../../hooks";
import { useTranslation } from "react-i18next";
import { getDisclaimerAgreeStatus } from "../../features/api/dts_api_wrapper";

interface QueryString {
  [key: string]: string;
}

/**
 * ログイン画面
 * @returns
 */
export const LoginSaml: React.FC = () => {
  //言語切替用
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleApi } = useApi();
  // エラーダイアログ
  const { showErrorDialog } = useErrorDialog();

  //Cognito ドメイン
  const cognito_base_url =
    process.env.REACT_APP_AWS_USER_POOLS_DOMAIN_NAME ?? "";
  // AWS Cognitoの認証エンドポイント
  const cognito_authorizetion_urn = cognito_base_url + "/oauth2/authorize";
  //   IDプロバイダ名
  const idp_name = process.env.REACT_APP_AWS_COGNITO_IDP_NAME;
  // 認証後にリダイレクトされるURI
  const redirect_uri =
    window.location.protocol +
    "//" +
    window.location.host +
    `/${PAGE_NAME_LOGIN}`;

  // AWS ユーザープールのアプリクライアントID
  const userpool_client_id =
    process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID ?? "";

  // ログインURL
  const login_uri =
    cognito_authorizetion_urn +
    "?identity_provider=" +
    idp_name +
    "&redirect_uri=" +
    redirect_uri +
    "&response_type=CODE&client_id=" +
    userpool_client_id;

  const appEnv = process.env.REACT_APP_ENV ?? "";

  //クエリパラメータをオブジェクトに変換
  const search = useLocation().search;
  const query = search
    .slice(1)
    .split("&")
    .map((str) => [str.split("=")[0], str.split("=")[1]])
    .reduce((acc, a) => {
      acc[a[0]] = a[1];
      return acc;
    }, {} as QueryString);

  //認証コード(認証成功の場合)
  const auth_code = query["code"];
  //エラーコード(認証失敗の場合)
  const error_code = query["error_description"];

  //ログイン処理
  const handleLogin = async () => {
    try {
      if (auth_code && !error_code) {
        //外部サイトで認証が成功した場合

        // リダイレクト時にわたってきた認可コードを利用し、トークン情報を取得
        const params = new URLSearchParams();
        params.append("grant_type", "authorization_code");
        params.append("redirect_uri", redirect_uri);
        params.append("code", auth_code);
        params.append("client_id", userpool_client_id);

        //トークン情報取得
        const tokenResponse = await fetch(cognito_base_url + "/oauth2/token", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: params,
        });
        // トークンの取得に失敗
        if (!tokenResponse.ok) {
          throw new Error("Token request failed");
        }
        const token = await tokenResponse.json();

        // アクセストークンを利用し、ユーザープールの情報を取得
        const bearer = "Bearer " + token.access_token;
        const userInfoResponse = await fetch(
          cognito_base_url + "/oauth2/userInfo",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              Authorization: bearer,
            },
          }
        );
        //ユーザー情報の取得に失敗
        if (!userInfoResponse.ok) {
          throw new Error("User info request failed");
        }
        const userInfo = await userInfoResponse.json();
        // Cognitoから取得した各種情報（トークンやユーザー情報など）をローカルストレージに保存
        console.log("Set token to localstorage.");
        //accessToken
        localStorage.setItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.accessToken`,
          token.access_token
        );
        //refreshToken
        localStorage.setItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.refreshToken`,
          token.refresh_token
        );
        //id_token
        localStorage.setItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.idToken`,
          token.id_token
        );
        //clockDrift
        localStorage.setItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.clockDrift`,
          "0"
        );
        //LastAuthUser
        localStorage.setItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.LastAuthUser`,
          userInfo.username
        );
        //スプラッシュ画像表示判定用の値をクリア
        sessionStorage.removeItem("splash");

        // 免責事項同意状態取得
        const getDisclaimerAgreeStatusResult = await handleApi(
          getDisclaimerAgreeStatus,
          userInfo.username
        );
        // エラー処理
        if ("errorMessage" in getDisclaimerAgreeStatusResult) {
          console.log(
            "errorMessage: ",
            getDisclaimerAgreeStatusResult.errorMessage
          );
          throw new Error(getDisclaimerAgreeStatusResult.errorMessage);
        }

        if (getDisclaimerAgreeStatusResult.agreeStatus === "0") {
          //免責事項同意状態：未同意の場合
          // 免責事項画面に遷移
          navigate(`/${PAGE_NAME_DISCLAIMER}`, {
            state: { isFromLogin: true }, //ログイン画面から遷移
          });
        } else if (getDisclaimerAgreeStatusResult.agreeStatus === "1") {
          //免責事項同意状態：同意の場合
          // トップ画面に遷移
          navigate(`/${PAGE_NAME_TOP}`, {
            state: { isFromLogin: true }, //ログイン画面から遷移
          });
        }
      } else if (error_code) {
        //外部サイトで認証に失敗した場合
        console.log("Login error");
        // 外部サイトで認証に失敗した場合の処理
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。"),
          "login"
        );
        return;
      } else {
        // リファラーでこの先の遷移先を判断
        const referrer = document.referrer;
        console.log("referrer: ", referrer);
        const ydsyos_uri = "https://yds-next.yanmar.com/";

        if (appEnv != "PRODUCT" || referrer.includes(ydsyos_uri)) {
          // Cognito経由でログイン処理
          window.location.assign(login_uri);
        } else {
          //リファラーがYDS/YOSでなければYDS/YOSに遷移
          window.location.assign(ydsyos_uri);
        }
      }
    } catch (error) {
      console.log("[handleLogin] error:", error);
      // 認証に失敗した場合の処理
      localStorage.removeItem(
        `CognitoIdentityServiceProvider.${userpool_client_id}.accessToken`
      );
      localStorage.removeItem(
        `CognitoIdentityServiceProvider.${userpool_client_id}.refreshToken`
      );
      localStorage.removeItem(
        `CognitoIdentityServiceProvider.${userpool_client_id}.idToken`
      );
      localStorage.removeItem(
        `CognitoIdentityServiceProvider.${userpool_client_id}.clockDrift`
      );
      localStorage.removeItem(
        `CognitoIdentityServiceProvider.${userpool_client_id}.LastAuthUser`
      );
      if (
        error instanceof Error &&
        (error.message === "Expired token" || error.message === "Invalid token")
      ) {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.セッションがタイムアウトしました。"),
          "ydsyos"
        ); //エラー", "セッションがタイムアウトしました。(YDS/YOSに遷移する)
      } else {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。"),
          "login"
        );
      }
      return;
    }
  };

  useEffect(() => {
    handleLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <></>;
};
